import proofkitFilemakerService from './proofkitFilemakerService.js';

// Obtener el valor de la variable de entorno
const LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_PLANES;

// Mostrar advertencia si la variable no está configurada correctamente
if (!LAYOUT || LAYOUT === 'your_filemaker_layout_planes') {
  console.warn('Advertencia: REACT_APP_FILEMAKER_LAYOUT_PLANES no está configurado correctamente. Valor actual:', LAYOUT);
}

export const getPlanes = async () => {
  try {
    // Verificar que LAYOUT esté definido
    if (!LAYOUT) {
      console.error('Error: REACT_APP_FILEMAKER_LAYOUT_PLANES no está definido');
      throw new Error('Configuración de layout no disponible');
    }
    
    // Obtener planes del layout Web_Planes (contiene fieldData + portalData)
    const response = await proofkitFilemakerService.getAllRecords(LAYOUT);
    const rows = Array.isArray(response?.data) ? response.data : [];

    if (rows.length === 0) {
      return [];
    }

    // Mapear los planes usando fieldData (datos principales) y portalData (items)
    return rows.map(planRecord => {
      // Los datos principales del plan están en fieldData
      const planData = planRecord.fieldData;
      
      // Los items están en portalData.plan
      const planItems = (planRecord.portalData?.plan || []).map(item => ({
        id: item['plan::id'],
        uuid: item['plan::uuid'],
        titulo: item['plan::titulo'],
        tipo: item['plan::tipo'],
        item: item['plan::item'],
        fkplanes: item['plan::fkplanes'],
        recordId: item.recordId,
        modId: item.modId
      }));
      
      // Construir el objeto del plan principal con sus items
      return {
        uuid: planData.uuid || planRecord.recordId,
        recordId: planRecord.recordId,
        modId: planRecord.modId,
        nombre: planData.nombre || '',
        descripcion: planData.descripcion || '',
        urlImagen: planData.urlImagen || '',
        badgeDestacador: planData.badgeDestacador || '',
        status: planData.status || 'active',
        urlContratacion: planData.urlContratacion || '',
        landbotConfig: planData.landbotConfig || '',
        fkFam: planData.fkFam || '',
        nota: planData.nota || '',
        fkUrl: planData.fkUrl || '',
        archived: planData.archived || 0,
        items: planItems,
        groupedItems: groupItemsByTypeAndTitle(planItems)
      };
    });
  } catch (error) {
    console.error('Error al obtener planes:', error);
    throw error;
  }
};

export const getPlanById = async (recordId) => {
  try {
    // Check if LAYOUT is defined
    if (!LAYOUT) {
      console.warn('Warning: REACT_APP_FILEMAKER_LAYOUT_PLANES is not defined');
      return null;
    }
    
    const response = await proofkitFilemakerService.getRecord(LAYOUT, recordId);
    const planData = response?.data;

    if (!planData) {
      console.warn('Unexpected API response structure:', response);
      return null;
    }
    
    // Map the response data to the expected format with all fields needed by PlanesForm and PlanesList
    return {
      id: planData.fieldData.id || '',
      recordId: planData.recordId || '',
      nombre: planData.fieldData.nombre || '',
      descripcion: planData.fieldData.descripcion || '',
      urlImagen: planData.fieldData.urlImagen || '',
      badgeDestacador: planData.fieldData.badgeDestacador || '',
      status: planData.fieldData.status || 'active',
      urlContratacion: planData.fieldData.urlContratacion || '',
      landbotConfig: planData.fieldData.landbotConfig || '',
      fkFam: planData.fieldData.fkFam || '',
      nota: planData.fieldData.nota || '',
      fkUrl: planData.fieldData.fkUrl || '',
      archived: planData.fieldData.archived || 0,
      // Include items from portalData
      items: planData.portalData && planData.portalData.plan 
        ? planData.portalData.plan.map(item => ({
            recordId: item.recordId || '',
            titulo: item["plan::titulo"] || '',
            tipo: item["plan::tipo"] || '',
            item: item["plan::item"] || '',
            fkplanes: item["plan::fkplanes"] || '',
            uuid: item["plan::uuid"] || '',
            id: item["plan::id"] || ''
          }))
        : []
    };
  } catch (error) {
    console.error(`Error fetching plan with ID ${recordId}:`, error);
    throw error;
  }
};

// Función auxiliar para agrupar items por tipo y título
const groupItemsByTypeAndTitle = (items) => {
  const grouped = {};
  
  items.forEach(item => {
    const key = `${item.tipo}-${item.titulo}`;
    if (!grouped[key]) {
      grouped[key] = {
        tipo: item.tipo,
        titulo: item.titulo,
        items: []
      };
    }
    grouped[key].items.push(item);
  });
  
  return Object.values(grouped);
};

export const createPlan = async (planData, items = []) => {
  try {
    // Preparar los items para portalData
    const portalItems = items.map(item => ({
      'plan::titulo': item.titulo,
      'plan::tipo': item.tipo,
      'plan::item': item.item
    }));

    // Asegurarse de que todos los campos necesarios estén incluidos
    // EXCLUIR urlContratacion y landbotConfig del payload de creación
    const completeFieldData = {
      nombre: planData.nombre || '',
      descripcion: planData.descripcion || '',
      urlImagen: planData.urlImagen || '',
      badgeDestacador: planData.badgeDestacador || '',
      status: planData.status || 'active',
      fkFam: planData.fkFam || '',
      nota: planData.nota || '',
      fkUrl: planData.fkUrl || ''
      // urlContratacion y landbotConfig REMOVIDOS del payload de creación
    };

    return await proofkitFilemakerService.createRecord(LAYOUT, completeFieldData, {
      portalData: {
        plan: portalItems
      }
    });
  } catch (error) {
    console.error('Error al crear plan:', error);
    throw error;
  }
};

export const updatePlan = async (recordId, planData, items = []) => {
  try {
    // Transform data to match the expected API structure
    const apiData = {};
    
    // Include only fields that are provided in planData and exist in the layout
    // EXCLUIR urlContratacion y landbotConfig del payload de actualización
    if (planData.nombre !== undefined) apiData.nombre = planData.nombre;
    if (planData.descripcion !== undefined) apiData.descripcion = planData.descripcion;
    if (planData.urlImagen !== undefined) apiData.urlImagen = planData.urlImagen;
    if (planData.badgeDestacador !== undefined) apiData.badgeDestacador = planData.badgeDestacador;
    if (planData.status !== undefined) apiData.status = planData.status;
    if (planData.fkFam !== undefined) apiData.fkFam = planData.fkFam;
    if (planData.fkUrl !== undefined) apiData.fkUrl = planData.fkUrl;
    if (planData.nota !== undefined) apiData.nota = planData.nota;
    // urlContratacion y landbotConfig REMOVIDOS del payload de actualización
    
    // Ensure archived is sent as a number (0 or 1)
    if (planData.archived !== undefined) {
      // If it's a boolean, convert to number
      if (typeof planData.archived === 'boolean') {
        apiData.archived = planData.archived ? 1 : 0;
      } else {
        // If it's already a number, use it directly
        apiData.archived = planData.archived;
      }
    }
    
    // Prepare the request payload
    const requestPayload = {
      fieldData: apiData
    };
    
    // Only include portalData if we have items to update
    if (items && items.length > 0) {
      // Format items for the portal data
      const formattedItems = items
        .filter(item => item.modified || item.deleted)
        .map(item => {
          const formattedItem = {};
          
          // For existing items that need modification, include the recordId with the proper format
          if (item.recordId) {
            formattedItem.recordId = item.recordId;
          }
          
          // For deleted items, only include recordId and recordAction
          if (item.deleted) {
            formattedItem.recordAction = 'delete';
            return formattedItem;
          }
          
          // For modified items, include the fields with the proper prefix
          formattedItem['plan::titulo'] = item.titulo || '';
          formattedItem['plan::tipo'] = item.tipo || 'titulo';
          formattedItem['plan::item'] = item.item || '';
          
          return formattedItem;
        });
      
      if (formattedItems.length > 0) {
        requestPayload.portalData = { plan: formattedItems };
      }
    }
    
    // Log the payload for debugging
    console.log('Update plan payload:', requestPayload);

    return await proofkitFilemakerService.updateRecord(LAYOUT, recordId, requestPayload.fieldData, {
      portalData: requestPayload.portalData
    });
  } catch (error) {
    console.error(`Error al actualizar plan con ID ${recordId}:`, error);
    throw error;
  }
};

export const deletePlan = async (recordId) => {
  try {
    return await proofkitFilemakerService.deleteRecord(LAYOUT, recordId);
  } catch (error) {
    console.error(`Error al eliminar plan con ID ${recordId}:`, error);
    throw error;
  }
};